import type { CitationOutput } from "@/components/tool-kit/source-citation-builder";
import {
  apaInTextAuthors,
  type CitationParts,
  mlaDate,
  organizationName,
  type PartialDate,
  tidyParts,
  withPeriod,
} from "@/lib/tool-kit/citation-parts";
import {
  apaPageLocator,
  readDate,
} from "@/lib/tool-kit/citation-sources-books/common";
import { titleCase } from "@/lib/tool-kit/citation-sources-more/common";
import {
  capitalizeSubtitle,
  closer,
  dayMonthYear,
  get,
  italic,
  mlaPageRange,
  mlaUrl,
  plain,
} from "@/lib/tool-kit/legal-sources/common";
import {
  bareCompany,
  beforeSubtitle,
  fullUrl,
  longDate,
  type MediaLabels,
  type MediaValues,
} from "@/lib/tool-kit/media-sources/common";
import {
  chicagoPageRange,
  publisherName,
  splitPages,
} from "@/lib/tool-kit/special-sources/common";

/*
 * A company's annual report, or one of its SEC filings (Form 10-K, 10-Q, 8-K, 20-F),
 * in APA 7, MLA 9, Chicago 18 and Harvard (Cite Them Right).
 *
 * Sources checked (October 2026). None of the style manuals prints an annual-report or
 * SEC-filing example, so each follows its own general model for a report by an
 * organization, as library guides apply it:
 * - APA 7: a report by an organization — "Company. (Year). Title in italics. Publisher.
 *   URL" — with the company as author, as Wayne State's and Butler's business guides
 *   do: "McDonald's Corporation. (2019). Form 10-K annual report for the fiscal year
 *   ended December 31, 2019." The "Retrieved from SEC EDGAR website" wording in those
 *   guides is APA 6; APA 7 names the SEC as the publisher instead. The year is the
 *   filing year, taken from the filing date (the Wayne State guide: the official date
 *   is at the end of the document). Publisher dropped when it is the author.
 * - MLA 9: the company is the author when someone else publishes the document (the
 *   SEC); when the company is also the publisher, MLA drops the author and begins with
 *   the title. Library guides give the SEC filing's date in full: "Form 10-K 2010.
 *   Securities and Exchange Commission EDGAR Online. Securities and Exchange
 *   Commission, 23 Jan. 2010, www.sec.gov/edgar.shtml." (University of South Florida).
 * - Chicago 18: the report pattern — organization, italic title, (publisher, date),
 *   page, URL — with no place of publication for a book or report after 1900 (CMOS
 *   14.30). Library guides differ on whether a 10-K's publisher is the company or the
 *   SEC; this puts the SEC, which hosts the document you read.
 * - Harvard: Cite Them Right (Pears and Shields, 13th ed., 2025), the most widely used UK variant, with no place of publication and (no date) in place of n.d.:
 *   "Author or organisation (Year of publication) Title of report. Reference number
 *   (if available). Publisher OR Available at: DOI or URL (Accessed: date)." with the
 *   in-text form (Organisation, Year, p. #) (Newcastle University's 2025 Cite Them
 *   Right sheet). Other universities' Harvard variants differ in punctuation and
 *   in-text style; follow your own institution's.
 * - Facts used in the worked examples, read from SEC EDGAR itself: Apple Inc.'s Form
 *   10-K for the fiscal year ended September 30, 2023, filed 3 November 2023
 *   (accession 0000320193-23-000106); Apple's Form 10-Q for the quarterly period ended
 *   December 30, 2023, filed 2 February 2024 (0000320193-24-000006). Microsoft
 *   Corporation's Annual Report 2023 is on microsoft.com/investor.
 */

export const ANNUAL_REPORT_FIELDS = {
  form: "form",
  company: "company",
  title: "title",
  period: "period",
  date: "date",
  publisher: "publisher",
  url: "url",
  accessed: "accessed",
  page: "page",
} as const;

const F = ANNUAL_REPORT_FIELDS;

/** A company's own annual report, or a filing made with the SEC. */
export type ReportKind = "annual" | "sec";

const FORMS = ["10-K", "10-Q", "8-K", "20-F"] as const;
export type SecForm = (typeof FORMS)[number];

const RE_EN_DASH = /–/g;

const SEC_PUBLISHER = "U.S. Securities and Exchange Commission";

export interface AnnualReportLabels extends MediaLabels {
  harvard: string;
  apaNote: string;
  mlaNote: string;
  chicagoNote: string;
  harvardNote: string;
}

interface Report {
  kind: ReportKind;
  form: SecForm;
  company: string;
  title: string;
  date: PartialDate | null;
  publisher: string;
  url: string;
  accessed: PartialDate | null;
  page: string;
}

function readForm(text: string): SecForm {
  return FORMS.find((form) => form === text) ?? "10-K";
}

/** The title a filing carries when none is typed: "Form 10-K: Annual report for…". */
function filingTitle(form: SecForm, period: PartialDate | null): string {
  const label = `Form ${form}`;
  const when = period ? longDate(period) : "";
  switch (form) {
    case "10-K":
    case "20-F":
      return when
        ? `${label}: Annual report for the fiscal year ended ${when}`
        : `${label}: Annual report`;
    case "10-Q":
      return when
        ? `${label}: Quarterly report for the quarterly period ended ${when}`
        : `${label}: Quarterly report`;
    default:
      return when
        ? `${label}: Current report dated ${when}`
        : `${label}: Current report`;
  }
}

function readReport(kind: ReportKind, values: MediaValues): Report {
  const sec = kind === "sec";
  const form = readForm(get(values, F.form));
  const typed = capitalizeSubtitle(get(values, F.title));
  const defaultPublisher = sec ? SEC_PUBLISHER : "";
  return {
    kind,
    form,
    company: get(values, F.company),
    title:
      typed || (sec ? filingTitle(form, readDate(get(values, F.period))) : ""),
    date: readDate(get(values, F.date)),
    publisher: get(values, F.publisher) || defaultPublisher,
    url: fullUrl(get(values, F.url)),
    accessed: readDate(get(values, F.accessed)),
    page: get(values, F.page),
  };
}

function yearText(report: Report): string {
  return report.date ? String(report.date.year) : "n.d.";
}

/** True when the publisher is the company itself ("Apple Inc." and "Apple"). */
function samePublisher(report: Report): boolean {
  const company = bareCompany(report.company).toLowerCase();
  const publisher = bareCompany(report.publisher).toLowerCase();
  return company !== "" && company === publisher;
}

/* ---------------------------------------------------------------------- APA */

function apaReference(report: Report): CitationParts {
  const publisher =
    report.publisher && !samePublisher(report)
      ? ` ${withPeriod(publisherName(report.publisher, "apa"))}`
      : "";
  const url = report.url ? ` ${report.url}` : "";
  const company = report.company ? `${withPeriod(report.company)} ` : "";
  return tidyParts([
    plain(`${company}(${yearText(report)}). `),
    italic(report.title),
    plain(`${closer(report.title)}${publisher}${url}`),
  ]);
}

function apaCitation(report: Report, narrative: boolean): CitationParts {
  const who = apaInTextAuthors([organizationName(report.company)], narrative);
  const page = report.page ? `, ${apaPageLocator(report.page)}` : "";
  const tail = `${yearText(report)}${page}`;
  return [plain(narrative ? `${who} (${tail})` : `(${who}, ${tail})`)];
}

function apaOutput(report: Report, labels: AnnualReportLabels): CitationOutput {
  return {
    key: "apa",
    label: labels.apa,
    entries: [
      { label: labels.referenceList, parts: apaReference(report) },
      { label: labels.parenthetical, parts: apaCitation(report, false) },
      { label: labels.narrative, parts: apaCitation(report, true) },
    ],
    note: labels.apaNote,
  };
}

/* ---------------------------------------------------------------------- MLA */

function mlaWorksCited(report: Report): CitationParts {
  const title = titleCase(report.title);
  const dropAuthor = samePublisher(report) || report.company === "";
  const lead = dropAuthor ? "" : `${withPeriod(report.company)} `;
  const publisher = report.publisher
    ? publisherName(report.publisher, "mla")
    : "";
  const link = report.url ? mlaUrl(report.url) : "";
  const tail = [publisher, report.date ? mlaDate(report.date) : "", link]
    .filter(Boolean)
    .join(", ");
  const accessed = report.accessed
    ? ` Accessed ${mlaDate(report.accessed)}.`
    : "";
  return tidyParts([
    plain(lead),
    italic(title),
    plain(`${closer(title)}${tail ? ` ${withPeriod(tail)}` : ""}${accessed}`),
  ]);
}

/** An in-text page or range in MLA's form: 12, 12-14, 398-402. */
function mlaPageText(text: string): string {
  const [from, to] = splitPages(text);
  return to ? mlaPageRange(from, to).replace(RE_EN_DASH, "-") : from;
}

function mlaOutput(report: Report, labels: AnnualReportLabels): CitationOutput {
  const dropAuthor = samePublisher(report) || report.company === "";
  const page = report.page ? mlaPageText(report.page) : "";
  const parts: CitationParts = dropAuthor
    ? [
        plain("("),
        italic(titleCase(beforeSubtitle(report.title))),
        plain(`${page ? ` ${page}` : ""})`),
      ]
    : [plain(`(${report.company}${page ? ` ${page}` : ""})`)];
  return {
    key: "mla",
    label: labels.mla,
    entries: [
      { label: labels.worksCited, parts: mlaWorksCited(report) },
      { label: labels.inText, parts },
    ],
    note: labels.mlaNote,
  };
}

/* ------------------------------------------------------------------ Chicago */

function chicagoDate(report: Report): string {
  return report.date ? longDate(report.date) : "n.d.";
}

function chicagoPages(text: string): string {
  const [from, to] = splitPages(text);
  return to ? chicagoPageRange(from, to) : from;
}

function chicagoNote(report: Report, title: string): CitationParts {
  const lead = report.company ? `${report.company}, ` : "";
  const imprint = [
    report.publisher ? publisherName(report.publisher, "chicago") : "",
    chicagoDate(report),
  ]
    .filter(Boolean)
    .join(", ");
  const tail = [report.page ? chicagoPages(report.page) : "", report.url]
    .filter(Boolean)
    .join(", ");
  return tidyParts([
    plain(lead),
    italic(title),
    plain(` (${imprint})${tail ? `, ${tail}` : ""}.`),
  ]);
}

function chicagoShortNote(report: Report, title: string): CitationParts {
  const lead = report.company ? `${report.company}, ` : "";
  const page = report.page ? `, ${chicagoPages(report.page)}` : "";
  return tidyParts([
    plain(lead),
    italic(beforeSubtitle(title)),
    plain(`${page}.`),
  ]);
}

function chicagoBibliography(report: Report, title: string): CitationParts {
  const lead = report.company ? `${withPeriod(report.company)} ` : "";
  const imprint = [
    report.publisher ? publisherName(report.publisher, "chicago") : "",
    chicagoDate(report),
  ]
    .filter(Boolean)
    .join(", ");
  const link = report.url ? ` ${report.url}.` : "";
  return tidyParts([
    plain(lead),
    italic(title),
    plain(`${closer(title)} ${withPeriod(imprint)}${link}`),
  ]);
}

function chicagoOutput(
  report: Report,
  labels: AnnualReportLabels
): CitationOutput {
  const title = titleCase(report.title);
  return {
    key: "chicago",
    label: labels.chicago,
    entries: [
      { label: labels.footnote, parts: chicagoNote(report, title) },
      { label: labels.shortNote, parts: chicagoShortNote(report, title) },
      { label: labels.bibliography, parts: chicagoBibliography(report, title) },
    ],
    note: labels.chicagoNote,
  };
}

/* ------------------------------------------------------------------ Harvard */

/** Cite Them Right writes (no date), not n.d. */
function harvardYear(report: Report): string {
  return report.date ? String(report.date.year) : "no date";
}

function harvardReference(report: Report): CitationParts {
  const company = report.company ? `${report.company} ` : "";
  const source = report.url
    ? `Available at: ${report.url}${report.accessed ? ` (Accessed: ${dayMonthYear(report.accessed)})` : ""}.`
    : withPeriod(
        report.publisher ? publisherName(report.publisher, "apa") : ""
      );
  return tidyParts([
    plain(`${company}(${harvardYear(report)}) `),
    italic(report.title),
    plain(`${closer(report.title)}${source ? ` ${source}` : ""}`),
  ]);
}

function harvardCitation(report: Report, narrative: boolean): CitationParts {
  const page = report.page ? `, ${apaPageLocator(report.page)}` : "";
  const who = report.company || beforeSubtitle(report.title);
  const tail = `${harvardYear(report)}${page}`;
  return [plain(narrative ? `${who} (${tail})` : `(${who}, ${tail})`)];
}

function harvardOutput(
  report: Report,
  labels: AnnualReportLabels
): CitationOutput {
  return {
    key: "harvard",
    label: labels.harvard,
    entries: [
      { label: labels.referenceList, parts: harvardReference(report) },
      { label: labels.parenthetical, parts: harvardCitation(report, false) },
      { label: labels.narrative, parts: harvardCitation(report, true) },
    ],
    note: labels.harvardNote,
  };
}

export function annualReportIsReady(
  kind: ReportKind,
  values: MediaValues
): boolean {
  return (
    get(values, F.company) !== "" &&
    (kind === "sec" || get(values, F.title) !== "")
  );
}

export function formatAnnualReport(
  kind: ReportKind,
  values: MediaValues,
  labels: AnnualReportLabels
): CitationOutput[] {
  const report = readReport(kind, values);
  return [
    apaOutput(report, labels),
    mlaOutput(report, labels),
    chicagoOutput(report, labels),
    harvardOutput(report, labels),
  ];
}
